import React, { useEffect, useState } from 'react';
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableSortLabel,
    Card,
    CardContent,
    Box,
    Button,
    Avatar,
    Typography,
    TableContainer,
    InputAdornment,
    TextField,
    CircularProgress,
    Badge,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    TablePagination,
    Alert,
    Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LabelIcon from '@mui/icons-material/Label';
import FilterListIcon from '@mui/icons-material/FilterList';
import { request } from '../../../../../utils/request';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDebounce } from '../../../../../utils/useDebounce';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';

import DeleteConfirmDialog from '../../../../../componentsUiBlock/DeleteConfirmDialog';
import { useDeleteDialog } from '../../../../../utils/useDeleteDialog';
import { Brand } from '../../../types';
import { baseUrlImage } from '../../../../../utils/apiClient';



interface HeadCell {
    id: keyof Brand | 'action';
    label: string;
}

const headCells: HeadCell[] = [
    { id: 'brandLogo', label: 'Logo' },
    { id: 'name', label: 'Brand Name' },
    { id: 'parentCompany', label: 'Parent Company' },
    { id: 'manufactureType', label: 'Manufacture Types' },
    { id: 'description', label: 'Description' },
    { id: 'action', label: 'Actions' }
];

const BrandsList: React.FC = () => {
    const [brands, setBrands] = useState<Brand[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState<keyof Brand>('name');
    const [search, setSearch] = useState('');

    const [manufactureTypeFilter, setManufactureTypeFilter] = useState<string>('all');
    const [availableManufactureTypes, setAvailableManufactureTypes] = useState<string[]>([]);

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const navigate = useNavigate();
    const debouncedSearch = useDebounce(search, 500);

    useEffect(() => {
        const fetchBrands = async () => {
            try {
                setLoading(true);
                let url = 'brand/get-all-brands';
                const params = new URLSearchParams();

                if (debouncedSearch) {
                    params.append('q', debouncedSearch);
                }
                if (manufactureTypeFilter && manufactureTypeFilter !== 'all') {
                    params.append('manufactureType', manufactureTypeFilter);
                }
                // Add pagination parameters
                params.append('page', (page + 1).toString());
                params.append('limit', rowsPerPage.toString());

                if (params.toString()) {
                    url += `?${params.toString()}`;
                }

                const res = await request({
                    url: url,
                    method: "get",
                });

                if (res.status === "success") {
                    setBrands(res.brands || []);
                    setTotal(res.total || 0);

                    // Extract unique manufacture types for filter dropdown
                    const allTypes = (res.brands || []).flatMap((brand: Brand) => brand.manufactureType || []);
                    const uniqueTypes = Array.from(new Set(allTypes)).sort();
                    setAvailableManufactureTypes(uniqueTypes as string[]);
                } else {
                    console.error('API response error:', res);
                    toast.error('Failed to load brands');
                }
            } catch (error) {
                console.error('Fetch brands error:', error);
                toast.error('Failed to load brands');
            } finally {
                setLoading(false);
            }
        };
        fetchBrands();
    }, [debouncedSearch, manufactureTypeFilter, page, rowsPerPage]);

    const handleSort = (property: HeadCell['id']) => {
        if (property === 'action') return;

        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property as keyof Brand);
    };

    // Client-side sorting for better UX
    const sortedBrands = [...brands].sort((a, b) => {
        const aValue = a[orderBy];
        const bValue = b[orderBy];

        if (Array.isArray(aValue) && Array.isArray(bValue)) {
            // Sort by first manufacture type for arrays
            const aFirst = aValue[0] || '';
            const bFirst = bValue[0] || '';
            return order === 'asc' ? aFirst.localeCompare(bFirst) : bFirst.localeCompare(aFirst);
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return order === 'asc'
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
        }
        return 0;
    });

    // Pagination handlers
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const deleteBrand = async (brand: Brand) => {
        try {
            const res = await request({
                url: `brand/delete-brand/${brand._id}`,
                method: "delete",
            });
            if (res?.status === "success") {
                toast.error(res?.message);
                setBrands((prev) => prev.filter((b) => b._id !== brand._id));
                setTotal(prev => prev - 1);

                // If we deleted the last item on the page, go to previous page
                if (brands.length === 1 && page > 0) {
                    setPage(page - 1);
                }
            } else {
                toast.error(res?.message || 'Failed to delete brand');
            }
        } catch (error: any) {
            toast.error(error)
        }

    }

    const { open, loadingDelete, itemToDelete, openDialog, closeDialog, confirmDelete } = useDeleteDialog<Brand>({
        onDelete: deleteBrand
    })

    const handleManufactureTypeFilterChange = (event: any) => {
        setManufactureTypeFilter(event.target.value);
        setPage(0); // Reset to first page when filter changes
    };

    const clearFilters = () => {
        setSearch('');
        setManufactureTypeFilter('all');
        setPage(0);
    };

    const getChipColor = (type: string) => {
        const colorMap: { [key: string]: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' } = {
            'Cars': 'info',
            'Parts': 'success'
        };
        return colorMap[type] || 'info';
    };

    return (
        <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                <Brandcrumb />
            </Box>
            <Card elevation={4}>
                <CardContent sx={{ pb: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                        <Button
                            variant="contained"
                            size='small'
                            color="primary"
                            onClick={() => navigate("/configuration/brands/addBrand")}
                            sx={{ flexShrink: 0 }}
                            startIcon={<LabelIcon />}
                        >
                            Add New Brand
                        </Button>

                        <Box display="flex" alignItems="center" flexWrap="wrap" gap={3}>
                            {/* Debug button - remove in production */}
                            {/* <Button variant="outlined" size="small" onClick={debugBrands}>
                                Debug
                            </Button> */}

                            {/* Filters Section */}
                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                {/* Manufacture Type Filter */}
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <InputLabel>
                                        <FilterListIcon sx={{ fontSize: 18, mr: 0.5 }} />
                                        Mfg. Type
                                    </InputLabel>
                                    <Select
                                        value={manufactureTypeFilter}
                                        label="Mfg. Type"
                                        onChange={handleManufactureTypeFilterChange}
                                    >
                                        <MenuItem value="all">All Types</MenuItem>
                                        {availableManufactureTypes.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>


                                <Badge badgeContent={total} showZero color="primary">
                                    <LabelIcon color="action" />
                                </Badge>

                                {/* Search Field */}
                                <TextField
                                    size="small"
                                    placeholder="Search by brand or company..."
                                    variant="outlined"
                                    sx={{ width: { xs: '100%', sm: 250 } }}
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setPage(0); // Reset to first page when search changes
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {/* Clear Filters Button */}
                                {(search || manufactureTypeFilter !== 'all') && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={clearFilters}
                                        sx={{ flexShrink: 0 }}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </Stack>
                        </Box>
                    </Box>
                </CardContent>

                <CardContent sx={{ pt: 1 }}>
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {headCells.map((head) => (
                                        <TableCell key={head.id} sx={{ fontWeight: 'bold' }}>
                                            {head.id !== 'action' ? (
                                                <TableSortLabel
                                                    active={orderBy === head.id}
                                                    direction={orderBy === head.id ? order : 'asc'}
                                                    onClick={() => handleSort(head.id)}
                                                >
                                                    {head.label}
                                                </TableSortLabel>
                                            ) : (
                                                head.label
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                            <CircularProgress size={24} />
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                {debouncedSearch || manufactureTypeFilter !== 'all' ? 'Searching...' : 'Loading brands...'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : sortedBrands.length > 0 ? (
                                    sortedBrands.map((brand) => (
                                        <TableRow key={brand._id} hover>
                                            {/* Brand Logo */}
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Avatar
                                                        variant="rounded"
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            bgcolor: 'background.paper',
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                        }}
                                                    >
                                                        <Box
                                                            component="img"
                                                            src={brand.brandLogo ? (baseUrlImage + brand.brandLogo) : 'https://cdn-icons-png.freepik.com/512/9942/9942310.png'}
                                                            alt={`${brand.name} Logo`}
                                                            sx={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'contain',
                                                                p: 0.5,
                                                            }}
                                                        // onError={(e) => {
                                                        //     // Fallback if image fails to load
                                                        //     (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40?text=Logo';
                                                        // }}
                                                        />
                                                    </Avatar>
                                                </Box>
                                            </TableCell>

                                            {/* Brand Name */}
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {brand.name}
                                                </Typography>
                                            </TableCell>

                                            {/* Parent Company */}
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {brand.parentCompany || '—'}
                                                </Typography>
                                            </TableCell>

                                            {/* Manufacture Types */}
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
                                                    {brand.manufactureType && brand.manufactureType.length > 0 ? (
                                                        brand.manufactureType.slice(0, 3).map((type, index) => (
                                                            <Chip
                                                                key={index}
                                                                label={type}
                                                                size="small"
                                                                color={getChipColor(type)}
                                                                variant="outlined"
                                                                sx={{ borderRadius: '12px' }} // Rounded chips
                                                            />
                                                        ))
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">
                                                            No types
                                                        </Typography>
                                                    )}
                                                    {brand.manufactureType && brand.manufactureType.length > 3 && (
                                                        <Chip
                                                            label={`+${brand.manufactureType.length - 3}`}
                                                            size="small"
                                                            variant="filled"
                                                            sx={{ borderRadius: '12px' }} // Rounded chips
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>

                                            {/* Description */}
                                            <TableCell sx={{ maxWidth: 200 }}>
                                                <Tooltip title={brand.description || '-'} arrow placement='top-start'>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: 'block'
                                                        }}
                                                    >
                                                        {brand.description || '-'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>

                                            {/* Action Buttons */}
                                            <TableCell>
                                                <Box display="flex" flexDirection="column" gap={0.8}>
                                                    <Button
                                                        variant="text"
                                                        onClick={() => navigate(`/configuration/brands/editBrand/${brand._id}`)}
                                                        sx={{
                                                            minWidth: 'auto',
                                                            fontSize: '0.60rem',
                                                            py: 0.1,
                                                            px: 2,
                                                            borderRadius: 5,
                                                            border: '.8px solid #7f5ee666',
                                                            '&:hover': { backgroundColor: '#7f5ee666' }
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        color="error"
                                                        onClick={() => openDialog(brand)}
                                                        sx={{
                                                            minWidth: 'auto',
                                                            fontSize: '0.60rem',
                                                            py: 0.1,
                                                            px: 2,
                                                            borderRadius: 5,
                                                            border: '.8px solid #ff4d6e5e',
                                                            '&:hover': { backgroundColor: '#ff4d6e5e' }
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                {search || manufactureTypeFilter !== 'all'
                                                    ? `No brands found matching your filters`
                                                    : 'No brands found. Add your first brand to get started.'}
                                            </Typography>
                                            {(search || manufactureTypeFilter !== 'all') && (
                                                <Button
                                                    variant="text"
                                                    onClick={clearFilters}
                                                    sx={{ mt: 1 }}
                                                >
                                                    Clear filters
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Material-UI TablePagination Component */}
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            borderTop: '1px solid',
                            borderColor: 'divider',
                        }}
                    />
                </CardContent>
            </Card>

            {/* Delete Confirmation Dialog */}

            <DeleteConfirmDialog
                open={open}
                loading={loadingDelete}
                title="Delete Brand"
                description={
                    <>
                        Are you sure you want to delete the brand <strong>"{itemToDelete?.name}"</strong>? <br /><br />
                        <Alert severity="warning" sx={{ fontSize: "12px", py: "0px" }}>This action cannot be undone and all associated data will be permanently removed.</Alert>
                    </>
                }
                onCancel={closeDialog}
                onConfirm={confirmDelete}
            />
        </>
    );
};

export default BrandsList;